import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, X, ChevronRight } from "lucide-react";

interface BusinessPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

type PanelLevel = "main" | "products" | "services" | "industries" | "marketplace";

const companyLinks = [
  { label: "Home", path: "/" },
  { label: "About", path: "/about" },
  { label: "Why VIATRA", path: "/why-viatra" },
  { label: "Contact", path: "/contact" },
];

const businessMenuItems = [
  { id: "products", label: "Products", hasSubmenu: true },
  { id: "services", label: "Services", hasSubmenu: true },
  { id: "industries", label: "Industries", hasSubmenu: true },
  { id: "marketplace", label: "Marketplace", hasSubmenu: true },
];

const productsItems = [
  { label: "Textiles & Garments", path: "/products#textiles" },
  { label: "Spices & Agro Products", path: "/products#spices" },
  { label: "Industrial Machinery", path: "/products#machinery" },
  { label: "Handicrafts & Décor", path: "/products#handicrafts" },
];

const servicesItems = [
  { label: "Global Sourcing", path: "/services#sourcing" },
  { label: "Export Management", path: "/services#export" },
  { label: "Quality Control", path: "/services#quality" },
  { label: "Logistics & Compliance", path: "/services#logistics" },
];

const industriesItems = [
  { label: "Retail & Distribution", path: "/industries#retail" },
  { label: "Manufacturing", path: "/industries#manufacturing" },
  { label: "Hospitality", path: "/industries#hospitality" },
  { label: "FMCG", path: "/industries#fmcg" },
];

const marketplaceItems = [
  { label: "For Buyers", path: "/for-buyers" },
  { label: "For Sellers", path: "/for-sellers" },
];

export function BusinessPanel({ isOpen, onClose }: BusinessPanelProps) {
  const [currentLevel, setCurrentLevel] = useState<PanelLevel>("main");
  const panelRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Lock body scroll when panel is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      // Save current focus and move to close button for accessibility
      previousFocusRef.current = document.activeElement as HTMLElement;
      closeButtonRef.current?.focus();
    } else {
      document.body.style.overflow = "unset";
      // Reset to main level when closing
      setTimeout(() => setCurrentLevel("main"), 300);
      // Restore focus
      previousFocusRef.current?.focus();
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // Handle ESC key to close panel and trap focus inside panel
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;
      
      // ESC key closes the panel
      if (event.key === "Escape") {
        onClose();
      }
      
      // Trap focus inside panel using Tab key
      if (event.key === "Tab" && panelRef.current) {
        const focusableElements = panelRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

        if (event.shiftKey) {
          if (document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  const handleBack = useCallback(() => {
    setCurrentLevel("main");
  }, []);

  const handleSubmenuClick = useCallback((id: string) => {
    setCurrentLevel(id as PanelLevel);
  }, []);

  const handleLinkClick = useCallback(() => {
    onClose();
  }, [onClose]);

  const getBreadcrumb = () => {
    switch (currentLevel) {
      case "products":
        return "Menu > Products";
      case "services":
        return "Menu > Services";
      case "industries":
        return "Menu > Industries";
      case "marketplace":
        return "Menu > Marketplace";
      default:
        return "Menu";
    }
  };

  const getCurrentItems = () => {
    switch (currentLevel) {
      case "products":
        return productsItems;
      case "services":
        return servicesItems;
      case "industries":
        return industriesItems;
      case "marketplace":
        return marketplaceItems;
      default:
        return [];
    }
  };

  const getViewAllLink = () => {
    switch (currentLevel) {
      case "products":
        return { label: "View All Products", path: "/products" };
      case "services":
        return { label: "View All Services", path: "/services" };
      case "industries":
        return { label: "View All Industries", path: "/industries" };
      case "marketplace":
        return null;
      default:
        return null;
    }
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 z-50 panel-overlay transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />

      {/* Panel - Slides from LEFT side */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        className={`fixed top-0 left-0 z-50 h-full w-full sm:w-[400px] md:w-[450px] bg-card border-r border-border shadow-lg transition-transform duration-300 ease-out ${
          isOpen ? "translate-x-0" : "translate-x-[-100%]"
        }`}
      >
        {/* Panel Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            {currentLevel !== "main" && (
              <button
                onClick={handleBack}
                className="p-2 hover:bg-secondary rounded-lg transition-colors"
                aria-label="Go back to main menu"
              >
                <ArrowLeft className="w-5 h-5 text-foreground" />
              </button>
            )}
            <span className="text-sm text-muted-foreground">{getBreadcrumb()}</span>
          </div>
          <button
            ref={closeButtonRef}
            onClick={onClose}
            className="p-2 hover:bg-secondary rounded-lg transition-colors"
            aria-label="Close navigation menu"
          >
            <X className="w-5 h-5 text-foreground" />
          </button>
        </div>

        {/* Panel Content */}
        <div className="p-6 overflow-y-auto h-[calc(100%-80px)]">
          {currentLevel === "main" ? (
            <div className="space-y-2">
              {/* Company Links - FIRST */}
              <div className="mb-6">
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-4">
                  Company
                </h3>
                {companyLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={handleLinkClick}
                    className="block p-4 rounded-lg hover:bg-secondary transition-colors text-lg text-foreground hover:text-primary"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>

              {/* Business Submenu Items - SECOND */}
              <div className="pt-6 border-t border-border">
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-4">
                  Business
                </h3>
                {businessMenuItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleSubmenuClick(item.id)}
                    className="w-full flex items-center justify-between p-4 rounded-lg hover:bg-secondary transition-colors group"
                  >
                    <span className="text-lg text-foreground group-hover:text-primary transition-colors">
                      {item.label}
                    </span>
                    <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  </button>
                ))}
              </div>

              {/* CTA */}
              <div className="pt-6">
                <Link
                  to="/contact"
                  onClick={handleLinkClick}
                  className="block w-full text-center p-4 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-gold-light transition-colors glow-gold"
                >
                  Partner With Us
                </Link>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {getCurrentItems().map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={handleLinkClick}
                  className="block p-4 rounded-lg hover:bg-secondary transition-colors text-lg text-foreground hover:text-primary"
                >
                  {item.label}
                </Link>
              ))}

              {/* View All Link */}
              {getViewAllLink() && (
                <Link
                  to={getViewAllLink()!.path}
                  onClick={handleLinkClick}
                  className="block p-4 mt-4 rounded-lg border border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors text-center font-medium"
                >
                  {getViewAllLink()!.label}
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

